import React, { useState } from 'react';
import { Link, useParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import BulkOrderForm from './BulkOrderForm';
import ContactForm from './ContactForm';

const SOCIAL_LINKS = [
  { name: 'Instagram', url: 'https://www.instagram.com/zelinastudios/', icon: (
    <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
  )},
  { name: 'Facebook', url: 'https://www.facebook.com/share/17mkcewnVG/', icon: (
    <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
  )},
  { name: 'Threads', url: 'https://www.threads.com/@zelinastudios', icon: (
    <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor"><path d="M12.186 24h-.007c-3.581-.024-6.334-1.205-8.184-3.509C2.35 18.44 1.5 15.586 1.472 12.01v-.017c.03-3.579.879-6.43 2.525-8.482C5.845 1.205 8.6.024 12.18 0h.014c2.746.02 5.043.725 6.826 2.098 1.677 1.29 2.858 3.13 3.509 5.467l-2.066.56c-1.08-3.87-3.709-5.979-7.407-6.112h-.002c-3.068-.005-5.332 1.022-6.79 3.058-1.27 1.77-1.93 4.275-1.955 7.44v.016c.026 3.164.686 5.67 1.96 7.441 1.456 2.023 3.72 3.05 6.789 3.058 2.052-.02 3.63-.556 4.967-1.69 1.476-1.252 2.07-2.878 2.07-4.481 0-1.126-.37-2.033-1.067-2.617-.625-.524-1.47-.795-2.404-.795-.753 0-1.475.179-2.085.513-.593.325-1.032.776-1.253 1.276l-.002.006c-.262.617-.295 1.258-.088 1.757.283.683.975 1.116 1.846 1.193l.005.02-.048.458c-.154.036-.315.056-.476.061-.898-.019-1.647-.404-2.106-1.086-.492-.73-.593-1.686-.29-2.653.39-1.253 1.595-2.248 3.302-2.248h.002c.918 0 1.788.174 2.56.512.768.335 1.42.826 1.922 1.45.944 1.17 1.432 2.745 1.432 4.442 0 2.224-.85 4.387-2.834 6.07-1.78 1.508-4.04 2.285-6.72 2.31z"/></svg>
  )},
  { name: 'Twitter', url: 'https://x.com/StudiosZel43348', icon: (
    <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
  )},
];

const Footer = () => {
  const { t } = useTranslation();
  const { lang } = useParams();
  const location = useLocation();
  const [bulkOpen, setBulkOpen] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);
  const prefix = `/${lang || 'en'}`;
  const path = location.pathname;
  const hideSubscribe = path.includes('/cart') || path.includes('/checkout') || path.includes('/account');

  const columns = [
    {
      title: t('footer.shop'),
      links: [
        { label: t('footer.straight'), to: `${prefix}/collections/straight` },
        { label: t('footer.wavy'), to: `${prefix}/collections/wavy` },
        { label: t('footer.curly'), to: `${prefix}/collections/curly` },
        { label: t('footer.shop_by_shade'), to: `${prefix}/shop-by-shade` },
      ],
    },
    {
      title: t('footer.support'),
      links: [
        { label: t('footer.shipping_info'), to: `${prefix}/policy/shipping-policy` },
        { label: t('footer.returns'), to: `${prefix}/policy/return-policy` },
        { label: 'Track Order', to: `${prefix}/track-order` },
        { label: t('footer.contact_us'), action: () => setContactOpen(true) },
        { label: 'Bulk Orders', action: () => setBulkOpen(true) },
      ],
    },
    {
      title: t('footer.zelina'),
      links: [
        { label: t('footer.our_story'), to: `${prefix}/about` },
        { label: t('footer.quality_promise'), to: `${prefix}/about` },
        { label: t('footer.hair_care_guide'), to: `${prefix}/hair-care-guide` },
      ],
    },
    {
      title: t('footer.legal'),
      links: [
        { label: t('footer.privacy_policy'), to: `${prefix}/policy/privacy-policy` },
        { label: t('footer.terms_conditions'), to: `${prefix}/policy/terms-conditions` },
        { label: t('footer.cookie_policy'), to: `${prefix}/policy/cookie-policy` },
      ],
    },
  ];

  return (
    <footer className="border-t py-20 px-6 md:px-10" style={{ background: '#0A0A0A', borderColor: '#1a1a1a', color: '#CBBFAF' }} data-testid="main-footer">
      <div className="max-w-6xl mx-auto">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-10 mb-16">
          {columns.map((col, idx) => (
            <div key={idx}>
              <h4 className="text-xs font-bold uppercase tracking-[0.15em] mb-6" style={{ color: '#C9A227' }}>{col.title}</h4>
              <ul className="space-y-3">
                {col.links.map((link) => (
                  <li key={link.label}>
                    {link.action ? (
                      <button onClick={link.action} className="text-sm transition-colors duration-300 hover:text-[#C9A227]" style={{ color: '#8a7e6b' }} data-testid={`footer-link-${link.label.toLowerCase().replace(/\s+/g, '-')}`}>{link.label}</button>
                    ) : link.href ? (
                      <a href={link.href} className="text-sm transition-colors duration-300 hover:text-[#C9A227]" style={{ color: '#8a7e6b' }} data-testid={`footer-link-${link.label.toLowerCase().replace(/\s+/g, '-')}`}>{link.label}</a>
                    ) : (
                      <Link to={link.to} className="text-sm transition-colors duration-300 hover:text-[#C9A227] flex items-center gap-2" style={{ color: '#8a7e6b' }} data-testid={`footer-link-${link.label.toLowerCase().replace(/\s+/g, '-')}`}>
                        {link.label}
                        {link.badge && <span className="text-[8px] uppercase tracking-wider px-1 py-0.5 rounded border border-[#C9A227]/30" style={{ color: '#C9A227' }}>{link.badge}</span>}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Newsletter in footer - hidden on Cart/Checkout/Account */}
        {!hideSubscribe && (
        <div className="py-8 mb-8 border-t border-b" style={{ borderColor: '#1a1a1a' }}>
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h4 className="text-sm font-semibold mb-1" style={{ color: '#F5F1E8' }}>{t('footer.join_community')}</h4>
              <p className="text-xs" style={{ color: '#8a7e6b' }}>{t('footer.community_desc')}</p>
            </div>
            <div className="flex items-center gap-2">
              <Link to={`${prefix}/register`} className="text-xs font-medium px-5 py-2.5 rounded-full transition-all duration-300 hover:shadow-lg hover:shadow-[#C9A227]/20 hover:-translate-y-px" style={{ background: '#C9A227', color: '#0A0A0A' }} data-testid="footer-join-btn">
                {t('footer.create_account')}
              </Link>
              <Link to={`${prefix}/login`} className="text-xs font-medium px-5 py-2.5 rounded-full border transition-all duration-300 hover:border-[#C9A227] hover:text-[#C9A227] hover:-translate-y-px" style={{ borderColor: '#333', color: '#CBBFAF' }} data-testid="footer-signin-btn">
                {t('footer.sign_in')}
              </Link>
            </div>
          </div>
          <p className="text-[10px] mt-4 text-center md:text-left" style={{ color: '#8a7e6b' }}>
            By creating an account, you agree to receive marketing communications from Zelina Studios. You can unsubscribe at any time. View our <Link to={`${prefix}/policy/privacy-policy`} className="underline hover:text-[#C9A227]" style={{ color: '#a09585' }}>Privacy Policy</Link>.
          </p>
        </div>
        )}

        <div className="flex flex-col md:flex-row items-center justify-between pt-4">
          <div className="flex flex-col items-center mb-4 md:mb-0">
            <img src="/zelina-logo.webp" alt="Zelina Studios" className="h-14 sm:h-16 opacity-50" decoding="async" />
          </div>
          <div className="flex items-center gap-4 mb-4 md:mb-0" data-testid="social-links">
            {SOCIAL_LINKS.map(s => (
              <a key={s.name} href={s.url} target="_blank" rel="noopener noreferrer"
                className="transition-all duration-300 hover:scale-110 hover:text-[#C9A227]"
                style={{ color: '#8a7e6b' }}
                aria-label={s.name}
                data-testid={`social-${s.name.toLowerCase()}`}
              >
                {s.icon}
              </a>
            ))}
          </div>
          <p className="text-xs" style={{ color: '#8a7e6b' }}>
            {t('footer.copyright', { year: new Date().getFullYear() })}
          </p>
        </div>
        <div className="text-center mt-6">
          <p className="text-[10px]" style={{ color: '#8a7e6b' }}>GSTIN: 33AAEFZ3145K1Z1</p>
        </div>
      </div>
      <BulkOrderForm isOpen={bulkOpen} onClose={() => setBulkOpen(false)} />
      <ContactForm isOpen={contactOpen} onClose={() => setContactOpen(false)} />
    </footer>
  );
};

export default Footer;
